import type { ChatMessage } from '../stores/chatStore'
import type { SpreadsheetEditOp, SpreadsheetSnapshotResponse } from './univer/types'
export type { SpreadsheetEditOp } from './univer/types'

const API_BASE = '/api'

function workspacePath(workspaceKey: string, suffix = ''): string {
  if (!workspaceKey) throw new Error('Workspace is required')
  return `/workspaces/${encodeURIComponent(workspaceKey)}${suffix}`
}

async function authFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { credentials: 'include', ...init })
  if (res.status === 401) {
    window.location.href = '/auth/login'
    throw new Error('Session expired')
  }
  if (!res.ok) {
    throw new Error(`Request failed (${res.status})`)
  }
  return res.json() as Promise<T>
}

export interface ApiConversation {
  id: string
  org_id: string
  user_id: string | null
  thesis_id: string | null
  title: string | null
  status: string
  last_active_at: string
  created_at: string
}

export interface ApiMessage {
  id: string
  conversation_id: string
  run_id: string | null
  role: 'user' | 'ai'
  content: string
  blocks: unknown
  seq: number
  created_at: string
}

export async function fetchConversations(workspaceKey: string): Promise<ApiConversation[]> {
  const r = await authFetch<{ conversations: ApiConversation[] }>(workspacePath(workspaceKey, '/conversations'))
  return r.conversations
}

export async function createConversation(
  workspaceKey: string,
  opts: { title?: string; from_conversation_id?: string; agent_name?: string } = {},
): Promise<{ id: string; created_at: string }> {
  return authFetch(workspacePath(workspaceKey, '/conversations'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts),
  })
}

export async function fetchConvDataSources(workspaceKey: string, conversationId: string): Promise<string[]> {
  const r = await authFetch<{ data_sources: string[] }>(
    workspacePath(workspaceKey, `/conversations/${conversationId}/data-sources`),
  )
  return r.data_sources
}
export function attachConvDataSource(workspaceKey: string, conversationId: string, sourceName: string): Promise<void> {
  return postJson(workspacePath(workspaceKey, `/conversations/${conversationId}/data-sources`), { source_name: sourceName })
}
export async function detachConvDataSource(workspaceKey: string, conversationId: string, sourceName: string): Promise<void> {
  await authFetch(workspacePath(workspaceKey, `/conversations/${conversationId}/data-sources/${encodeURIComponent(sourceName)}`), { method: 'DELETE' })
}

export async function fetchMessages(workspaceKey: string, conversationId: string): Promise<ApiMessage[]> {
  const r = await authFetch<{ messages: ApiMessage[] }>(
    workspacePath(workspaceKey, `/conversations/${conversationId}/messages`),
  )
  return r.messages
}

export interface ApiConvChart {
  id: string
  conversation_id: string
  message_id: string | null
  artifact_id: string
  version_num: number
  parent_version_id: string | null
  title: string
  spec: Record<string, unknown>
  data: { columns?: { name: string; dtype: string }[]; rows?: Record<string, unknown>[] }
  metadata: unknown
  produced_by: unknown
  saved_by_user: boolean
  created_at: string
}

export interface ApiConvResult {
  id: string
  conversation_id: string
  message_id: string | null
  title: string
  columns: unknown
  rows: unknown
  row_count: number
  metadata: unknown
  produced_by: unknown
  saved_by_user: boolean
  created_at: string
}

export async function fetchConversationCharts(workspaceKey: string, conversationId: string): Promise<ApiConvChart[]> {
  const r = await authFetch<{ charts: ApiConvChart[] }>(
    workspacePath(workspaceKey, `/conversations/${conversationId}/charts`),
  )
  return r.charts
}

export async function fetchConversationResults(workspaceKey: string, conversationId: string): Promise<ApiConvResult[]> {
  const r = await authFetch<{ results: ApiConvResult[] }>(
    workspacePath(workspaceKey, `/conversations/${conversationId}/results`),
  )
  return r.results
}

export function getResultCsvUrl(workspaceKey: string, resultId: string): string {
  return `${API_BASE}${workspacePath(workspaceKey, `/results/${resultId}/csv`)}`
}

export interface ApiConvSpreadsheet {
  id: string
  conversation_id: string
  message_id: string | null
  artifact_id: string
  version_num: number
  parent_version_id: string | null
  title: string
  byte_size: number
  sha256: string
  produced_by: unknown
  saved_by_user: boolean
  created_at: string
}

export async function fetchConversationSpreadsheets(workspaceKey: string, conversationId: string): Promise<ApiConvSpreadsheet[]> {
  const r = await authFetch<{ spreadsheets: ApiConvSpreadsheet[] }>(
    workspacePath(workspaceKey, `/conversations/${conversationId}/spreadsheets`),
  )
  return r.spreadsheets
}

export function spreadsheetDownloadUrl(workspaceKey: string, id: string): string {
  return `${API_BASE}${workspacePath(workspaceKey, `/spreadsheets/${id}/download`)}`
}

export function spreadsheetSnapshotUrl(workspaceKey: string, id: string): string {
  return `${API_BASE}${workspacePath(workspaceKey, `/spreadsheets/${id}/snapshot`)}`
}

export async function fetchSpreadsheetSnapshot(url: string): Promise<SpreadsheetSnapshotResponse> {
  const res = await fetch(url, { credentials: 'include' })
  if (res.status === 401) {
    window.location.href = '/auth/login'
    throw new Error('Session expired')
  }
  if (!res.ok) throw new Error(`Failed to fetch spreadsheet snapshot: ${res.status}`)
  return res.json() as Promise<SpreadsheetSnapshotResponse>
}

export async function saveSpreadsheetVersion(
  workspaceKey: string,
  artifactId: string,
  baseVersion: number,
  ops: SpreadsheetEditOp[],
): Promise<{ id: string; version_num: number }> {
  return authFetch(workspacePath(workspaceKey, `/spreadsheets/${artifactId}/versions`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ base_version: baseVersion, ops }),
  })
}

// --- Artifact gallery (org-wide) -------------------------------------------

export async function fetchAllCharts(workspaceKey: string): Promise<ApiConvChart[]> {
  const r = await authFetch<{ charts: ApiConvChart[] }>(workspacePath(workspaceKey, '/charts'))
  return r.charts
}
export async function fetchAllResults(workspaceKey: string): Promise<ApiConvResult[]> {
  const r = await authFetch<{ results: ApiConvResult[] }>(workspacePath(workspaceKey, '/results'))
  return r.results
}
export async function fetchAllSpreadsheets(workspaceKey: string): Promise<ApiConvSpreadsheet[]> {
  const r = await authFetch<{ spreadsheets: ApiConvSpreadsheet[] }>(workspacePath(workspaceKey, '/spreadsheets'))
  return r.spreadsheets
}

async function postJson(path: string, body: unknown): Promise<void> {
  await authFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}
async function patchJson(path: string, body: unknown): Promise<void> {
  await authFetch(path, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}

export type ArtifactKind = 'chart' | 'result' | 'spreadsheet'

// id = artifact_id for chart/spreadsheet, result-row id for result.
export function setArtifactSaved(workspaceKey: string, kind: ArtifactKind, id: string, saved: boolean): Promise<void> {
  const seg = kind === 'chart' ? 'charts' : kind === 'result' ? 'results' : 'spreadsheets'
  return postJson(workspacePath(workspaceKey, `/${seg}/${id}/saved`), { saved })
}
export function renameArtifact(workspaceKey: string, kind: ArtifactKind, id: string, title: string): Promise<void> {
  const seg = kind === 'chart' ? 'charts' : kind === 'result' ? 'results' : 'spreadsheets'
  return patchJson(workspacePath(workspaceKey, `/${seg}/${id}`), { title })
}

// OLD-named wrappers so the ported artifact components compile unchanged.
// chart/spreadsheet rename + save are by artifact_id; result by row id.
export const renameChart = (workspaceKey: string, artifactId: string, title: string) => renameArtifact(workspaceKey, 'chart', artifactId, title)
export const renameResult = (workspaceKey: string, resultId: string, title: string) => renameArtifact(workspaceKey, 'result', resultId, title)
export const renameSpreadsheet = (workspaceKey: string, artifactId: string, title: string) => renameArtifact(workspaceKey, 'spreadsheet', artifactId, title)
export const saveChart = (workspaceKey: string, artifactId: string) => setArtifactSaved(workspaceKey, 'chart', artifactId, true)
export const saveResult = (workspaceKey: string, resultId: string) => setArtifactSaved(workspaceKey, 'result', resultId, true)
export const saveSpreadsheet = (workspaceKey: string, artifactId: string) => setArtifactSaved(workspaceKey, 'spreadsheet', artifactId, true)

export async function fetchSpreadsheetBytes(url: string): Promise<ArrayBuffer> {
  const res = await fetch(url, { credentials: 'include' })
  if (!res.ok) throw new Error(`Failed to fetch spreadsheet bytes: ${res.status}`)
  return res.arrayBuffer()
}

export class StaleBaseVersionError extends Error {
  constructor(msg = 'stale base_version') {
    super(msg)
    this.name = 'StaleBaseVersionError'
  }
}

// Apply Univer edit ops as a new version. `force` overrides an older base.
export async function postSpreadsheetVersion(
  workspaceKey: string,
  artifactId: string,
  body: { base_version: number; ops: SpreadsheetEditOp[]; force?: boolean },
): Promise<{ id: string; version_num: number }> {
  const res = await fetch(`${API_BASE}${workspacePath(workspaceKey, `/spreadsheets/${artifactId}/versions`)}`, {
    credentials: 'include',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (res.status === 401) {
    window.location.href = '/auth/login'
    throw new Error('Session expired')
  }
  if (res.status === 409) throw new StaleBaseVersionError()
  if (!res.ok) throw new Error(`Save failed (${res.status})`)
  return res.json() as Promise<{ id: string; version_num: number }>
}

export function mapApiMessageToStore(m: ApiMessage): ChatMessage {
  // AI messages persist their ordered text/thinking/tool blocks so the
  // thought-process collapsible survives a reload / post-turn refetch.
  const blocks =
    m.role === 'ai' && Array.isArray(m.blocks) ? (m.blocks as ChatMessage['blocks']) : undefined
  const attachments =
    m.role === 'user' && Array.isArray(m.blocks)
      ? m.blocks
          .filter((b): b is { type: string; mime_type: string; data_base64: string } =>
            typeof b === 'object' &&
            b != null &&
            (b as { type?: unknown }).type === 'image_attachment' &&
            typeof (b as { mime_type?: unknown }).mime_type === 'string' &&
            typeof (b as { data_base64?: unknown }).data_base64 === 'string',
          )
          .map((b) => ({ mimeType: b.mime_type, dataBase64: b.data_base64 }))
      : undefined
  return {
    id: m.id,
    dbId: m.id,
    role: m.role,
    content: m.content,
    blocks,
    attachments,
    timestamp: new Date(m.created_at).getTime(),
  }
}
